import { sealIndexFields, openIndexFields } from './crypto';

// Der lokale Index (Datei- und Ordnernamen, Dateitypen, Namen laufender Uploads) liegt in
// IndexedDB nur verschluesselt. Bis 22.10.26 standen dort die Namen im Klartext und blieben
// ueber das Sperren und den Browserneustart hinaus auf der Platte.
//
// Bewusst an den Schreib- und Lesestellen und nicht als Dexie-Zwischenschicht: WebCrypto ist
// asynchron, und ein await auf crypto.subtle innerhalb einer IndexedDB-Transaktion laesst die
// Transaktion vorzeitig abschliessen (TransactionInactiveError).

const SEALED_FIELDS = {
  file: ['name', 'type', 'size', 'originalType'],
  folder: ['name'],
  pending: ['fileName', 'originalType']
};

let keyProvider = () => null;

/** Die App sagt hier, woher der Indexschluessel kommt (Schluesselbund des offenen Tresors). */
export function setIndexKeyProvider(fn) {
  keyProvider = typeof fn === 'function' ? fn : () => null;
}

function currentIndexKey() {
  const key = keyProvider();
  if (!key) throw new Error('INDEX_LOCKED');
  return key;
}

/** Datensatz fuer IndexedDB: geschuetzte Felder raus, verschluesselt in `sealed`. */
export async function sealRecord(kind, record) {
  const fields = SEALED_FIELDS[kind];
  const secret = {};
  const rest = { ...record };
  for (const f of fields) {
    if (f in rest) {
      secret[f] = rest[f];
      rest[f] = null;
    }
  }
  rest.sealed = await sealIndexFields(currentIndexKey(), secret);
  return rest;
}

/** Datensatz aus IndexedDB lesbar machen. Alte, unverschluesselte Eintraege kommen unveraendert zurueck. */
export async function openRecord(kind, record) {
  if (!record || !record.sealed) return record;
  const secret = await openIndexFields(currentIndexKey(), record.sealed);
  const { sealed, ...rest } = record;
  for (const f of SEALED_FIELDS[kind]) if (f in secret) rest[f] = secret[f];
  return rest;
}

export async function openRecords(kind, records) {
  const out = [];
  for (const r of records) out.push(await openRecord(kind, r));
  return out;
}

/**
 * Aenderung fuer db.<table>.update(). Beruehrt sie ein geschuetztes Feld, wird der ganze
 * geschuetzte Teil neu verschluesselt (aus dem aktuellen Datensatz plus Aenderung).
 */
export async function sealPatch(kind, currentRaw, patch) {
  const fields = SEALED_FIELDS[kind];
  if (!fields.some((f) => f in patch)) return patch;
  const current = (await openRecord(kind, currentRaw)) || {};
  const merged = {};
  for (const f of fields) merged[f] = f in patch ? patch[f] : current[f];
  const sealed = await sealRecord(kind, merged);
  const out = { ...patch, sealed: sealed.sealed };
  for (const f of fields) out[f] = null;
  return out;
}

/** Bestandseintraege aus der Zeit vor der Verschluesselung einmal umschreiben. */
export async function sealLegacyRows(db) {
  let changed = 0;
  for (const [table, kind, probe] of [[db.files, 'file', 'name'], [db.folders, 'folder', 'name'], [db.pendingUploads, 'pending', 'fileName']]) {
    const rows = await table.toArray();
    for (const row of rows) {
      if (row.sealed || row[probe] == null) continue;
      await table.put(await sealRecord(kind, row));
      changed += 1;
    }
  }
  return changed;
}
